import { useCallback, useEffect, useRef, useState } from "react";
import { getAiGenerationUsage } from "services/pmService";
import { identitySubscriptionService } from "services/subscriptions";

const PLAN_LIMITS = {
  FREE: 10,
  STANDARD: 100,
  PREMIUM: null, // unlimited
};

function resolveLimit(planCodes) {
  if (!planCodes || planCodes.length === 0) return PLAN_LIMITS.FREE;
  const upper = planCodes.map((c) => c.toUpperCase());
  if (upper.some((c) => c.includes("PREMIUM"))) return PLAN_LIMITS.PREMIUM;
  if (upper.some((c) => c.includes("STANDARD"))) return PLAN_LIMITS.STANDARD;
  return PLAN_LIMITS.FREE;
}

export default function useAiGenerationUsage(workspaceId) {
  const [data, setData] = useState({
    used: 0,
    limit: null,
    remaining: null,
    loading: true,
    error: null,
    hasReachedLimit: false,
  });
  const intervalRef = useRef(null);

  const refetch = useCallback(async () => {
    if (!workspaceId) {
      setData({
        used: 0,
        limit: 10,
        remaining: 10,
        loading: false,
        error: null,
        hasReachedLimit: false,
      });
      return;
    }

    try {
      const [usageResult, planCodesResult] = await Promise.all([
        getAiGenerationUsage(workspaceId).catch(() => null),
        identitySubscriptionService.plans
          .list({ code: "OVACUS", status: "ACTIVE" })
          .then((res) => {
            const content = res?.data?.content || res?.content || [];
            return content.map((p) => p.code || p.name || "");
          })
          .catch(() => []),
      ]);

      const resolvedLimit = resolveLimit(planCodesResult);
      const limit = usageResult?.limit ?? resolvedLimit;
      const used = usageResult?.used ?? 0;

      // If usage endpoint returned data, trust its limit; otherwise use plan-codes result
      const effectiveLimit = usageResult ? usageResult.limit : resolvedLimit;
      const effectiveUsed = usageResult ? usageResult.used : 0;

      // For premium (unlimited), set limit to null
      const finalLimit = effectiveLimit === null ? null : effectiveLimit;
      const remaining = finalLimit === null ? null : Math.max(0, finalLimit - effectiveUsed);
      const hasReachedLimit = finalLimit !== null && effectiveUsed >= finalLimit;

      setData({
        used: effectiveUsed,
        limit: finalLimit,
        remaining,
        loading: false,
        error: null,
        hasReachedLimit,
      });
    } catch (err) {
      // On error, fall back to FREE plan limits
      setData({
        used: 0,
        limit: PLAN_LIMITS.FREE,
        remaining: PLAN_LIMITS.FREE,
        loading: false,
        error: err?.message || "Failed to fetch usage",
        hasReachedLimit: false,
      });
    }
  }, [workspaceId]);

  useEffect(() => {
    refetch();

    // Poll every 60s to keep usage in sync
    intervalRef.current = setInterval(refetch, 60_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refetch]);

  return { ...data, refetch };
}
